import type { AccountInfo } from '@azure/msal-browser';
import { CommonError } from '@common/models/error.model';
import {
  AUTH_SERVICE,
  LoginProviderType,
  LoginResult,
} from '@common/services/auth/auth.service.interface';
import BasicAuthService from '@common/services/auth/basic-auth.service.impl';
import { LOG_SERVICE } from '@common/services/log/log.service.interface';
import envConfig from '@scoring-web/configs/env/env.config';
import { i18n } from '@scoring-web/configs/locale/locale.config';
import userStore from '@scoring-web/modules/user/stores/user.store';
import { User } from '@user/models/user.model';
import { container } from 'tsyringe';
import { join } from 'urlcat';

export default class AuthService extends BasicAuthService {
  constructor() {
    super();

    this.scopes = envConfig.msAzureAdScope.split(';');

    this.initializeHandleRedirect = this.initializeHandleRedirect.bind(this);
  }

  protected async initializeHandleRedirect() {
    await this.initialize();

    if (!this.auth) return;

    try {
      const response = await this.auth.handleRedirectPromise();

      if (!response?.account || !response?.accessToken) return;
      userStore.actions.updateToken(response.accessToken);

      if (!response?.account) return;
      const user = this.mapAccountToUser(response.account, response.accessToken);
      if (user) userStore.actions.setUser(user);
    } catch {
      // Do nothing
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async getAuthConfigurations() {
    const loginRoute = await import('@scoring-web/modules/user/views/login/login.route');
    const frameTimeout = 10000;
    return {
      auth: {
        authority: envConfig.msAzureAdAuthority,
        clientId: envConfig.msAzureAdClientId,
        knownAuthorities: envConfig.msAzureAdKnownAuthority.split(';'),
        redirectUri: join(window.location.origin, '/', loginRoute.default.path),
      },
      cache: { cacheLocation: 'localStorage' },
      system: {
        iframeHashTimeout: frameTimeout,
        loadFrameTimeout: frameTimeout,
        windowHashTimeout: frameTimeout,
      },
    };
  }

  // eslint-disable-next-line class-methods-use-this
  private handleLoggingError(error: unknown): void {
    if (error instanceof Error) {
      container
        .resolve(LOG_SERVICE)
        .captureError(error, undefined, { source: AUTH_SERVICE.toString() });
    } else {
      container
        .resolve(LOG_SERVICE)
        .captureError(
          new Error(AUTH_SERVICE.toString()),
          { error },
          { source: AUTH_SERVICE.toString() },
        );
    }
  }

  // eslint-disable-next-line class-methods-use-this
  protected async handleError(error: unknown): Promise<never | null> {
    const { BrowserAuthError, BrowserAuthErrorCodes } = await import('@azure/msal-browser');

    if (error instanceof BrowserAuthError) {
      if (error.errorCode === BrowserAuthErrorCodes.userCancelled) return null;
      if (error.errorCode === BrowserAuthErrorCodes.popupWindowError) {
        throw new CommonError({
          message: i18n.t('error.login-popup'),
          messageTranslationKey: 'error.login-popup',
        });
      }
    }

    this.handleLoggingError(error);

    throw error;
  }

  protected async getTokenFromAccount(account: AccountInfo | null): Promise<string | null> {
    if (!this.auth) return null;

    const tokenRequest = { account: account ?? undefined, scopes: this.scopes };

    try {
      const response = await this.auth.acquireTokenSilent(tokenRequest);
      if (!response) return null;

      return response.accessToken;
    } catch (error) {
      const { InteractionRequiredAuthError } = await import('@azure/msal-browser');
      if (!(error instanceof InteractionRequiredAuthError)) this.handleLoggingError(error);

      return null;
    }
  }

  async getUserInfo(): Promise<User | null> {
    await this.initialize();

    if (!this.auth) return null;

    const account = this.auth.getActiveAccount();
    if (!account) return null;

    return this.mapAccountToUser(account, userStore.state.token);
  }

  async loginProvider(loginType?: LoginProviderType): Promise<LoginResult | null> {
    userStore.actions.setLoginType(loginType);

    return super.loginProvider(loginType);
  }

  async logout(): Promise<void> {
    return super.logout(userStore.state.loginType);
  }
}
